import React, { useState } from "react";
import { Leaf, BarChart3, Shield } from "lucide-react";

const LandingPage = ({ onUserDataSave }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    cnpj: "",
    position: "",
    company_description: "",
  });

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "cnpj") {
      const formatted = formatCNPJ(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Basic validation
      if (!formData.name || !formData.email || !formData.cnpj || !formData.position || !formData.company_description) {
        setError("Por favor, preencha todos os campos.");
        setLoading(false);
        return;
      }

      // Validate CNPJ has 14 digits
      const cnpjNumbers = formData.cnpj.replace(/\D/g, '');
      if (cnpjNumbers.length !== 14) {
        setError("CNPJ deve conter 14 dígitos.");
        setLoading(false);
        return;
      }

      // Validate corporate email
      const freeEmailDomains = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'live.com', 'icloud.com'];
      const emailDomain = formData.email.split('@')[1]?.toLowerCase();
      if (freeEmailDomains.includes(emailDomain)) {
        setError("E-mail corporativo é obrigatório. E-mails gratuitos não são permitidos.");
        setLoading(false);
        return;
      }

      // Save to localStorage
      const userData = {
        name: formData.name,
        email: formData.email.toLowerCase(),
        cnpj: cnpjNumbers, // Store only numbers
        position: formData.position,
        company_description: formData.company_description,
      };

      onUserDataSave(userData);
      setShowAuthModal(false);
    } catch (err) {
      setError("Ocorreu um erro. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-container">
      <header className="landing-header">
        <div className="header-content">
          <div className="logo" data-testid="logo">ESG Assessment</div>
        </div>
      </header>

      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            Avalie a maturidade ESG da sua startup
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Um questionário completo para medir as práticas ambientais, sociais
            e de governança da sua empresa, com resultados detalhados e
            orientações para evolução.
          </p>
          <button
            className="cta-button"
            onClick={() => setShowAuthModal(true)}
            data-testid="hero-cta-btn"
          >
            Começar Avaliação
          </button>
        </div>
      </section>

      <section className="features-section">
        <div className="features-grid">
          <div className="feature-card" data-testid="feature-environmental">
            <div className="feature-icon">
              <Leaf size={28} strokeWidth={2} />
            </div>
            <h3 className="feature-title">Pilar Ambiental</h3>
            <p className="feature-description">
              Avalie gestão de energia, consumo de recursos, trabalho
              sustentável e cadeia de suprimentos.
            </p>
          </div>

          <div className="feature-card" data-testid="feature-social">
            <div className="feature-icon">
              <BarChart3 size={28} strokeWidth={2} />
            </div>
            <h3 className="feature-title">Pilar Social</h3>
            <p className="feature-description">
              Meça práticas de saúde, diversidade, desenvolvimento e
              relacionamento com stakeholders.
            </p>
          </div>

          <div className="feature-card" data-testid="feature-governance">
            <div className="feature-icon">
              <Shield size={28} strokeWidth={2} />
            </div>
            <h3 className="feature-title">Pilar Governança</h3>
            <p className="feature-description">
              Analise ética, controles internos, segurança da informação e
              estrutura de governança.
            </p>
          </div>
        </div>
      </section>

      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
            <button
              className="close-modal-btn"
              onClick={() => setShowAuthModal(false)}
              data-testid="close-auth-modal-btn"
            >
              ×
            </button>

            <div className="auth-modal-header">
              <h2 className="auth-modal-title" data-testid="auth-modal-title">
                Criar conta
              </h2>
              <p className="auth-modal-subtitle">
                Cadastre-se para iniciar sua avaliação ESG
              </p>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="name">
                  Nome completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="position">
                  Cargo *
                </label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  className="form-input"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  data-testid="register-position-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  E-mail corporativo *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="auth-email-input"
                />
                <small style={{ fontSize: "0.8rem", color: "#999" }}>
                  E-mails gratuitos (Gmail, Hotmail, etc.) não são aceitos
                </small>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cnpj">
                  CNPJ *
                </label>
                <input
                  type="text"
                  id="cnpj"
                  name="cnpj"
                  className="form-input"
                  value={formData.cnpj}
                  onChange={handleInputChange}
                  required
                  maxLength={18}
                  placeholder="00.000.000/0000-00"
                  data-testid="register-cnpj-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="company_description">
                  O que sua empresa faz? *
                </label>
                <textarea
                  id="company_description"
                  name="company_description"
                  className="form-textarea"
                  value={formData.company_description}
                  onChange={handleInputChange}
                  maxLength={250}
                  required
                  placeholder="Descreva brevemente a atividade principal da sua empresa..."
                  data-testid="register-company-desc-input"
                />
                <div className="char-counter">
                  {formData.company_description.length}/250
                </div>
              </div>

              {error && <div className="form-error" data-testid="auth-error-message">{error}</div>}

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? "Processando..." : "Criar Conta"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
